import { memo, useEffect, useRef } from "react";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import { AppState } from "@Store/index";
import Loader from "@Components/Loader";
import wrapperHOC from "@Utils/wrapperHOC";
import formatClassNames from "@Utils/clientUtils/stringUtility/formatClassNames";
import useTranslationHook from "hooks/useTranslationHook";
import useImpressionOnce from "hooks/useImpressionOnce";
import { PAGE } from "constants/configConstants";
import SearchListItem from "../SearchListItem";
import SearchListItemDesktopV2 from "../SearchListItem/SearchListItemDesktopV2";
import { setRecommendationsInView } from "../../data/searchPageReducer";
import useRecommendations from "./useRecommendations";
import styles from "./recommendedBeyondFilters.module.scss";

type Props = {
  /** Desktop list view renders the desktop card; grid and mobile render the mobile card. */
  isDesktop?: boolean;
  isGridView?: boolean;
  /** Desktop map sidebar: one column, and the map pins these properties while they are seen. */
  isMapView?: boolean;
};

const RecommendedBeyondFilters = ({
  isDesktop = false,
  isGridView = false,
  isMapView = false,
}: Props) => {
  const { t } = useTranslationHook();
  const dispatch = useDispatch();
  const sectionRef = useRef<HTMLElement>(null);
  // Impression is measured on the header, not the section: with ten cards the section is far
  // taller than any viewport, so a 50% visibility threshold on it could never be reached.
  const headerRef = useRef<HTMLDivElement>(null);
  const { visible, items, pageLoading, mainItemsLength, onSectionSeen, cardContextFor } =
    useRecommendations({ isDesktop: isDesktop || isGridView });

  const { region, customLocation, whiteLabelPartnerData } = useSelector(
    ({ searchPageData }: AppState) => ({
      region: searchPageData.region,
      customLocation: searchPageData.customLocation,
      whiteLabelPartnerData: searchPageData.whiteLabelPartnerData,
    }),
    shallowEqual,
  );

  useImpressionOnce(headerRef, onSectionSeen, { enabled: visible });

  // Map view: pin these properties only while the section is on screen (any part of it).
  useEffect(() => {
    const el = sectionRef.current;
    if (!isMapView || !el || typeof IntersectionObserver === "undefined") return undefined;
    const observer = new IntersectionObserver(([entry]) =>
      dispatch(setRecommendationsInView(entry.isIntersecting)),
    );
    observer.observe(el);
    return () => {
      observer.disconnect();
      dispatch(setRecommendationsInView(false));
    };
  }, [isMapView, visible, dispatch]);

  if (!visible) return null;

  const Card = isDesktop ? SearchListItemDesktopV2 : SearchListItem;

  return (
    <section
      ref={sectionRef}
      className={styles.section}
      aria-labelledby="search-recommendations-heading"
      data-testid="search-recommendations"
    >
      <div ref={headerRef} className={styles.header}>
        <h2 id="search-recommendations-heading" className={styles.heading}>
          {t("searchpage.recommendations.heading")}
        </h2>
        <p className={styles.subheading}>{t("searchpage.recommendations.subheading")}</p>
      </div>

      <div
        className={formatClassNames(
          isGridView ? styles.cardsGrid : styles.cards,
          isMapView && styles.mapView,
        )}
      >
        {items.map((item: any, i: number) => (
          <Card
            key={item.id}
            index={mainItemsLength + i}
            {...item}
            regionType={region?.region_type}
            regionId={region?.id}
            region={region}
            meta={item.meta_short}
            meta_real={item.meta}
            page={PAGE.SEARCH}
            isGridView={isGridView}
            loadedCount={mainItemsLength + items.length}
            customLocation={customLocation}
            whiteLabelPartnerData={whiteLabelPartnerData}
            recommendation={cardContextFor(item, i)}
          />
        ))}
      </div>

      {pageLoading && (
        <div className={styles.loader} aria-hidden="true">
          <Loader noShadow />
        </div>
      )}
    </section>
  );
};

export default wrapperHOC(memo(RecommendedBeyondFilters), {
  componentName: "RecommendedBeyondFilters",
  showForChina: true,
  showForWhiteLable: false,
  showForApp: true,
  showForMiniProgram: false,
  height: "0px",
});
